import { Callout } from '@carbon/react';
import { formatNumber } from 'utils/number';
import styles from './wells-duplicates-banner.module.scss';

export interface WellsDuplicatesBannerProps {
  /**
   * The detection run's count of wells flagged as potential duplicates, run-wide
   * rather than scoped to the current filter. Read off the wells listing response.
   */
  totalDuplicateCount: number;
}

/**
 * A standing advisory above the wells results: the latest detection run flagged some
 * wells as potential duplicates of another.
 *
 * The count is run-wide (not scoped to the current filter or page), so the copy stays a
 * neutral heads-up and does NOT tell the reader to act on the rows on screen — a
 * filtered or paged view may show no flagged rows at all. The per-row broom in the
 * Potential Duplicates column is the actual entry point. A Callout rather than a
 * notification so it reads as a standing advisory and never takes focus.
 * @param {WellsDuplicatesBannerProps} props The run-wide flagged-well count.
 * @return {JSX.Element | null} The banner, or nothing when none were flagged.
 */
export function WellsDuplicatesBanner({
  totalDuplicateCount,
}: WellsDuplicatesBannerProps) {
  if (totalDuplicateCount < 1) return null;

  return (
    <Callout
      className={styles.banner}
      kind="info"
      lowContrast
      title="Potential duplicates"
      subtitle={
        totalDuplicateCount === 1
          ? '1 well in your data looks like a duplicate of another.'
          : `${formatNumber(totalDuplicateCount, 0)} wells in your data look like duplicates of another.`
      }
    />
  );
}
